import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardBody, Input, Button, Label } from "reactstrap";
import { toast } from "react-toastify";
import Loaders from "components/Common/Loaders";
import {
    extractCallDetailsRenderedTemplateHtml,
    renderCallDetailsConversationTemplate,
    sendCallDetailsConversationMessage,
} from "model/CallModel";

const defaultDemoMessages = [
    {
        id: 1,
        sender: "bot",
        text: "Hello Nice",
        time: "02:10 PM",
    },
    {
        id: 2,
        sender: "bot",
        text:
            "Welcome to LiveChat\nI was made with Pick a topic from the list or type down a question!",
        time: "02:10 PM",
    },
    {
        id: 3,
        sender: "user",
        text: "Welcome",
        time: "02:12 PM",
        read: true,
    },
];

const MessageModal = ({
    messageModalOpenClose,
    setMessageModalOpenClose,
    messageRef,
    messages: messagesProp,
    loading = false,
    readOnly = false,
    callDetailsId = "",
    templates = [],
    onConversationRefresh,
    candidateDetails
}) => {
    const isCallDetailsConversation = Boolean(String(callDetailsId ?? "").trim());

    const [input, setInput] = useState("");
    const [messageInput, setMessageInput] = useState("");
    const [localMessages, setLocalMessages] = useState(defaultDemoMessages);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [templateRendering, setTemplateRendering] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = useCallback((behavior = "smooth") => {
        messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
    }, []);

    const showTemplatePreview =
        isCallDetailsConversation &&
        String(messageInput ?? "").includes("<") &&
        String(messageInput ?? "").trim() !== "";

    useEffect(() => {
        if (!messageModalOpenClose) return;
        if (Array.isArray(messagesProp)) {
            setLocalMessages(messagesProp);
        } else if (!isCallDetailsConversation) {
            setLocalMessages(defaultDemoMessages);
        } else {
            setLocalMessages([]);
        }
    }, [messageModalOpenClose, messagesProp, isCallDetailsConversation]);

    useEffect(() => {
        if (!messageModalOpenClose) {
            setSelectedTemplateId("");
            setMessageInput("");
            setTemplateRendering(false);
            setSendingMessage(false);
            setInput("");
        }
    }, [messageModalOpenClose]);

    useEffect(() => {
        if (!messageModalOpenClose || loading || !localMessages.length) return;
        const frameId = requestAnimationFrame(() => {
            scrollToBottom("smooth");
        });
        return () => cancelAnimationFrame(frameId);
    }, [localMessages, messageModalOpenClose, loading, scrollToBottom]);

    const handleTemplateChange = async (event) => {
        const templateId = String(event?.target?.value ?? "").trim();
        setSelectedTemplateId(templateId);
        if (!templateId) {
            return;
        }

        setTemplateRendering(true);
        try {
            const response = await renderCallDetailsConversationTemplate({
                callDetailsId,
                templateId,
            });
            if (response?.success === false) {
                toast.error(response?.message || "Unable to render template.");
                return;
            }
            const html = extractCallDetailsRenderedTemplateHtml(response);
            if (!html) {
                toast.error("Template rendered empty.");
                return;
            }
            setMessageInput(html);
        } catch {
            toast.error("Unable to render template.");
        } finally {
            setTemplateRendering(false);
        }
    };

    const handleSendCallDetailsMessage = async () => {
        const message = String(messageInput ?? "").trim();
        if (!message) {
            toast.error("Please enter a message or select a template.");
            return;
        }

        setSendingMessage(true);
        try {
            const response = await sendCallDetailsConversationMessage({
                callDetailsId,
                message,
            });
            const ok =
                response?.success ||
                response?.isSuccess ||
                response?.status === "success" ||
                response?.status === true;
            if (!ok) {
                toast.error(response?.message || "Unable to send message.");
                return;
            }
            // toast.success(response?.message || "Message sent.");
            setSelectedTemplateId("");
            setMessageInput("");
            if (typeof onConversationRefresh === "function") {
                await onConversationRefresh();
            }
        } catch {
            toast.error("Unable to send message.");
        } finally {
            setSendingMessage(false);
        }
    };

    const sendMessage = () => {
        if (readOnly || !input.trim()) return;

        setLocalMessages((prev) => [
            ...prev,
            {
                id: Date.now(),
                sender: "user",
                text: input,
                time: new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                read: true,
            },
        ]);
        setInput("");
    };

    if (!messageModalOpenClose) return null;

    const renderChatText = (msg) => {
        const text = String(msg?.text ?? "");
        const isHtmlMessage = /<[a-z][\s\S]*>/i.test(text);
        if (isCallDetailsConversation && isHtmlMessage) {
            return (
                <div
                    className="chat-text"
                    dangerouslySetInnerHTML={{ __html: text }}
                />
            );
        }
        return <div className="chat-text">{text}</div>;
    };

    const formatMessageMeta = (msg) => {
        const label = String(msg?.displayFrom ?? "").trim();
        const time = String(msg?.time ?? "").trim();
        if (label && time) return `${label == 'Candidate' ? `${candidateDetails?.firstName ?? ""} ${candidateDetails?.lastName ?? ""}` : label} · ${time}`;
        return label || time || "";
    };

    return (
        <>
            <div
                className="message-backdrop"
                onClick={() => setMessageModalOpenClose(false)}
            />

            <div
                className={
                    isCallDetailsConversation
                        ? "message-modal message-modal--call-details call-details-pos"
                        : "message-modal call-details-pos"
                }
            >
                <div className="message-modal-outer">
                    <Card className="message-card shadow-none mb-0 border-0">
                        <CardBody className="message-body">
                            {loading ? (
                                <div className="message-loading-wrap">
                                    <Loaders />
                                </div>
                            ) : !localMessages.length ? (
                                <div className="message-empty-state">
                                   <span className="d-flex justify-content-center align-items-center">  No conversation messages yet.</span>
                                </div>
                            ) : (
                                localMessages.map((msg) => (
                                    <div
                                        key={msg.id}
                                        className={`chat-row ${
                                            msg.sender === "user" ? "user" : "bot"
                                        }`}
                                    >
                                        {msg.sender === "bot" && (
                                            <div className="chat-icon">
                                                <i className="fas fa-comment-dots" />
                                            </div>
                                        )}

                                        <div className="chat-bubble">
                                            {renderChatText(msg)}
                                            <div
                                                className={`chat-time${
                                                    isCallDetailsConversation
                                                        ? " chat-time--call-details"
                                                        : ""
                                                }`}
                                            >
                                                {isCallDetailsConversation
                                                    ? formatMessageMeta(msg)
                                                    : msg.sender === "bot"
                                                      ? `Livechat ${msg.time}`
                                                      : `Visitor ${msg.time}`}
                                            </div>
                                            {msg.read && msg.sender === "user" && (
                                                <div className="chat-read">Read</div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                            {!loading && localMessages.length > 0 ? (
                                <div
                                    ref={messagesEndRef}
                                    className="message-scroll-anchor"
                                    aria-hidden="true"
                                />
                            ) : null}
                        </CardBody>

                        {isCallDetailsConversation ? (
                            <div className="chat-compose-area chat-compose-area--call-details">
                                <div className="chat-compose-template-row">
                                    <Label className="chat-compose-template-label">
                                        Template (optional)
                                    </Label>
                                    <Input
                                        type="select"
                                        className="chat-compose-template-select"
                                        value={selectedTemplateId}
                                        onChange={handleTemplateChange}
                                        disabled={templateRendering || sendingMessage}
                                    >
                                        <option value="">Select template</option>
                                        {(Array.isArray(templates) ? templates : []).map(
                                            (template) => (
                                                <option
                                                    key={template.value}
                                                    value={template.value}
                                                >
                                                    {template.label}
                                                </option>
                                            )
                                        )}
                                    </Input>
                                </div>

                                {/* {templateRendering ? (
                                    <div className="chat-compose-loading">
                                        <Loaders />
                                    </div>
                                ) : null} */}

                                {showTemplatePreview ? (
                                    <div
                                        className="chat-compose-preview"
                                        dangerouslySetInnerHTML={{
                                            __html: messageInput,
                                        }}
                                    />
                                ) : null}

                                <div className="chat-input-area chat-input-area--call-details">
                                    <Input
                                        type="textarea"
                                        className="chat-compose-message-input"
                                        placeholder="Send a Message"
                                        value={messageInput}
                                        onChange={(e) =>
                                            setMessageInput(e.target.value)
                                        }
                                        disabled={sendingMessage || templateRendering}
                                    />
                                    <Button
                                        color="primary"
                                        className="chat-send-btn"
                                        onClick={handleSendCallDetailsMessage}
                                        disabled={
                                            sendingMessage ||
                                            templateRendering ||
                                            !String(messageInput ?? "").trim()
                                        }
                                    >
                                        {sendingMessage ? (
                                            "Sending…"
                                        ) : (
                                            <>
                                                <i className="fa fa-paper-plane" />
                                                <span className="chat-send-btn-label">
                                                    Send
                                                </span>
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="chat-input-area">
                                <Input
                                    placeholder="Send a Message"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) =>
                                        e.key === "Enter" && sendMessage()
                                    }
                                />
                                <Button color="primary" onClick={sendMessage}>
                                    <i className="fa fa-paper-plane" />
                                    <span className="chat-send-btn-label">Send</span>
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </>
    );
};

export default MessageModal;
